// frontend/src/components/InactivityWarningModal.jsx
import { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';

/**
 * Modal component that warns user about impending auto-logout
 * @param {boolean} show - Whether to show the modal
 * @param {function} onStayLoggedIn - Callback when user clicks "Stay Logged In"
 * @param {function} onLogout - Callback when user clicks "Logout"
 * @param {number} countdownSeconds - Seconds until auto-logout (default: 300 = 5 minutes)
 */
const InactivityWarningModal = ({
                                    show,
                                    onStayLoggedIn,
                                    onLogout,
                                    countdownSeconds = 300
                                }) => {
    const [secondsRemaining, setSecondsRemaining] = useState(countdownSeconds);

    useEffect(() => {
        if (show) {
            setSecondsRemaining(countdownSeconds);

            const interval = setInterval(() => {
                setSecondsRemaining((prev) => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [show, countdownSeconds]);

    // Auto-logout when countdown reaches 0
    useEffect(() => {
        if (show && secondsRemaining === 0) {
            onLogout();
        }
    }, [secondsRemaining, show, onLogout]);

    // Format seconds as MM:SS
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <Modal
            show={show}
            onHide={onStayLoggedIn}
            backdrop="static"
            keyboard={false}
            centered
        >
            <Modal.Header>
                <Modal.Title>
                    <i className="fas fa-clock"></i> Session Timeout Warning
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>
                    You've been inactive for a while. For your security, you'll be
                    automatically logged out in:
                </p>
                <div className="text-center my-4">
                    <h2 className="text-danger">
                        <i className="fas fa-hourglass-half"></i> {formatTime(secondsRemaining)}
                    </h2>
                </div>
                <p className="mb-0">
                    Click <strong>"Stay Logged In"</strong> to continue your session,
                    or <strong>"Logout Now"</strong> to logout immediately.
                </p>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    type='button'
                    variant='primary'
                    className='btn-block blue-outline'
                    onClick={onLogout}
                    style={{ width: '48%' }}
                >
                    <i className="fas fa-sign-out-alt"></i> Logout Now
                </Button>
                <Button
                    type='button'
                    variant='info'
                    className='btn-block orange-outline'
                    onClick={onStayLoggedIn}
                    style={{ width: '48%' }}
                >
                    <i className="fas fa-check"></i> Stay Logged In
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default InactivityWarningModal;